import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { getBrowserLanguage, getTranslations } from '../../components/translations';
import { isUnifespInstitution } from '../../pages/tree/TreeProfile/components/Coletas/samplingUtils';

// Relative path assets from src/layouts/Header/
import googleIco from '../../pages/welcome/Google_Symbol_0.svg';
import ico_map from '../../icons/map.svg';
import ico_gallery from '../../icons/gallery.svg';
import ico_notifications from '../../icons/notifications.svg';
import ico_community from '../../icons/community.svg';
import placeholderAvatar from '../../icons/placeholder-avatar.png';

const basicLogo = process.env.PUBLIC_URL + '/icons/novo_logo.png';

export default function Header({ authProps }) {
  const {
    user,
    isAdmin,
    acceptUserTerms,
    notificationsCount,
    photoURL,
    displayFinal,
    signInWithGoogle,
    handleSignOut,
  } = authProps;

  const [texts, setTexts] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pendingReportsCount, setPendingReportsCount] = useState(0);
  const [pendingCommunicationCount, setPendingCommunicationCount] = useState(0);
  const [isPrefeitura, setIsPrefeitura] = useState(false);
  const [isProManagement, setIsProManagement] = useState(false);
  const [isProUser, setIsProUser] = useState(false);
  const [isProTecnico, setIsProTecnico] = useState(false);
  const [isProViveiro, setIsProViveiro] = useState(false);
  const [isUnifespWorkgroup, setIsUnifespWorkgroup] = useState(false);
  const [workgroupLabel, setWorkgroupLabel] = useState("PRO");
  const [allowedFeatures, setAllowedFeatures] = useState({ teamManagement: true, confidentialFields: true });
  const dropdownRef = useRef(null);

  useEffect(() => {
    const browserLang = getBrowserLanguage();
    const translations = getTranslations(browserLang);
    setTexts(translations.menu || {});
  }, []);

  // Obter cargo para verificar se é PRO-PREFEITURA ou PRO-VIVEIRO
  useEffect(() => {
    if (!user) {
      setIsPrefeitura(false);
      setIsProManagement(false);
      setIsProUser(false);
      setIsProTecnico(false);
      setIsProViveiro(false);
      setIsUnifespWorkgroup(false);
      setWorkgroupLabel("PRO");
      setAllowedFeatures({ teamManagement: true, confidentialFields: true });
      return;
    }
    user.getIdTokenResult(true)
      .then(async (result) => {
        const role = result.claims?.role;
        setIsPrefeitura(role === "pro_prefeitura" || role === "city_hall" || role === "pro_educacao" || role === "pro_ambiente");
        setIsProManagement(["pro_prefeitura", "city_hall", "pro_educacao", "pro_escola", "pro_ambiente", "pro_professor", "pro_viveiro", "pro_universidade", "pro_institutional", "pro_tecnico"].includes(role));
        setIsProTecnico(role === "pro_tecnico");
        setIsProViveiro(role === "pro_viveiro" || Boolean(result.claims?.parentProViveiroId));

        const proRoles = ['pro', 'pro_professional', 'pro_teacher', 'pro_school', 'city_hall', 'pro_institutional', 'admin', 'pro_prefeitura', 'pro_educacao', 'pro_ambiente', 'pro_analista', 'pro_viveiro', 'pro_universidade', 'pro_tecnico'];
        setIsProUser(proRoles.includes(role));

        let features = (role === "pro_professional" || role === "user")
          ? { teamManagement: false, confidentialFields: false }
          : { teamManagement: true, confidentialFields: true };

        const UNIFESP_UID = "qFvaPXkigUQmeLQAkinOecPeoY63";
        let unifespDetected =
          user.uid === UNIFESP_UID ||
          user.email === "lab.carboplant@gmail.com" ||
          result.claims?.parentProViveiroId === UNIFESP_UID ||
          isUnifespInstitution(role) ||
          isUnifespInstitution(result.claims?.universityName);

        // Fetch custom label name (Unifesp, etc.) and allowed features
        try {
          if (role === "pro_tecnico") {
            const parentId = result.claims?.parentProViveiroId;
            if (parentId) {
              if (parentId === UNIFESP_UID) unifespDetected = true;
              const managerDocRef = doc(db, 'users_public', parentId);
              const managerDocSnap = await getDoc(managerDocRef);
              if (managerDocSnap.exists()) {
                const managerData = managerDocSnap.data();
                if (managerData.role === "pro_universidade" || Boolean(managerData.universityName)) {
                  setIsProViveiro(false);
                }
                const name = managerData.universityName || managerData.nurseryName || managerData.companyName || managerData.customDisplayName || managerData.displayName || "PRO";
                setWorkgroupLabel(name);
                if (
                  isUnifespInstitution(name) ||
                  isUnifespInstitution(managerData.universityName) ||
                  isUnifespInstitution(managerData.companyName)
                ) {
                  unifespDetected = true;
                }
                if (managerData.allowedFeatures) {
                  features = managerData.allowedFeatures;
                }
              }
            }
          } else if (["pro_universidade", "pro_viveiro", "pro_institutional", "pro_prefeitura", "city_hall", "pro_educacao", "pro_ambiente"].includes(role)) {
            const userDocRef = doc(db, 'users_public', user.uid);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              const name = userData.universityName || userData.nurseryName || userData.companyName || userData.proCityName || userData.customDisplayName || userData.displayName || "PRO";
              setWorkgroupLabel(name);
              if (
                isUnifespInstitution(name) ||
                isUnifespInstitution(userData.universityName) ||
                isUnifespInstitution(userData.companyName)
              ) {
                unifespDetected = true;
              }
              if (userData.allowedFeatures) {
                features = userData.allowedFeatures;
              }
            } else {
              // Fallback to private users collection
              const privateUserRef = doc(db, 'users', user.uid);
              const privateUserSnap = await getDoc(privateUserRef);
              if (privateUserSnap.exists()) {
                const privateData = privateUserSnap.data();
                if (
                  isUnifespInstitution(privateData.universityName) ||
                  isUnifespInstitution(privateData.companyName)
                ) {
                  unifespDetected = true;
                }
                if (privateData.allowedFeatures) {
                  features = privateData.allowedFeatures;
                }
              }
            }
          } else if (role === "pro_prefeitura" || role === "city_hall" || role === "pro_educacao" || role === "pro_ambiente") {
            const cityName = result.claims?.proCityName || "PRO";
            setWorkgroupLabel(cityName);
          } else {
            setWorkgroupLabel("PRO");
          }
        } catch (err) {
          console.warn("Erro ao obter nome do grupo de trabalho/permissões no Header:", err);
          setWorkgroupLabel("PRO");
        }

        // Garante que Unifesp/Carboplant e perfis pro_universidade nunca tenham campos fechados ocultos
        if (unifespDetected || role === "pro_universidade") {
          features = {
            ...features,
            confidentialFields: true,
            teamManagement: true,
          };
        }

        setIsUnifespWorkgroup(unifespDetected);
        setAllowedFeatures(features);
      })
      .catch((err) => {
        console.warn("Erro ao obter custom claims no Header:", err);
        setIsPrefeitura(false);
        setIsProManagement(false);
        setIsProUser(false);
        setIsProTecnico(false);
        setIsProViveiro(false);
        setIsUnifespWorkgroup(false);
        setWorkgroupLabel("PRO");
        setAllowedFeatures({ teamManagement: true, confidentialFields: true });
      });
  }, [user]);

  // Escutar denúncias pendentes se o usuário for Admin
  useEffect(() => {
    if (!isAdmin) return;

    try {
      const unsub = onSnapshot(collection(db, "moderation"), (snap) => {
        const pending = snap.docs.filter((d) => {
          const data = d.data();
          if (data.status === "resolved") return false;
          const isMultiUser = data.description === "Duplicated Tree" || data.description === "Wrong location";
          if (isMultiUser && data.waitmoderation !== true) return false;
          return true;
        }).length;
        setPendingReportsCount(pending);
      }, (err) => {
        console.warn("Erro ao buscar denúncias pendentes no Header:", err);
      });
      return () => unsub();
    } catch (e) {
      console.warn("Erro no listener de moderação do Header:", e);
    }
  }, [isAdmin]);

  // Escutar mensagens de comunicação pendentes se o usuário for Admin
  useEffect(() => {
    if (!isAdmin) return;

    try {
      const unsub = onSnapshot(collection(db, "conversations"), (snap) => {
        const pending = snap.docs.filter((d) => {
          const data = d.data();
          return data.status === "pending";
        }).length;
        setPendingCommunicationCount(pending);
      }, (err) => {
        console.warn("Erro ao buscar conversas pendentes no Header:", err);
      });
      return () => unsub();
    } catch (e) {
      console.warn("Erro no listener de comunicação do Header:", e);
    }
  }, [isAdmin]);

  const location = useLocation();
  const isAdminPath =
    (location.pathname === '/admin' ||
      location.pathname.startsWith('/admin/') ||
      location.pathname.startsWith('/search-edit-page')) &&
    !location.pathname.startsWith('/admin-porte') &&
    !location.pathname.startsWith('/admin-front-page');

  const isOnAdminPage = location.pathname.startsWith('/admin-front-page');
  const [isAdminMenu, setIsAdminMenu] = useState(() => {
    if (isOnAdminPage) return true;
    try {
      return localStorage.getItem('maptrees_last_menu_page') === 'admin';
    } catch {
      return false;
    }
  });
  const [isViveiroMenu, setIsViveiroMenu] = useState(() => {
    try {
      return localStorage.getItem('maptrees_last_menu_page') === 'viveiro';
    } catch {
      return false;
    }
  });

  // Sincronizar estado de página ao carregar perfil
  useEffect(() => {
    if (isProViveiro) {
      try {
        if (localStorage.getItem('maptrees_last_menu_page') === 'viveiro') {
          setIsViveiroMenu(true);
        }
      } catch (e) {}
    }
  }, [isProViveiro]);

  useEffect(() => {
    if (isAdmin) {
      try {
        if (localStorage.getItem('maptrees_last_menu_page') === 'admin') {
          setIsAdminMenu(true);
        }
      } catch (e) {}
    }
  }, [isAdmin]);

  // Sincronizar estado de modo admin com rota
  useEffect(() => {
    if (location.pathname.startsWith('/admin-front-page')) {
      setIsAdminMenu(true);
      try {
        localStorage.setItem('maptrees_last_menu_page', 'admin');
      } catch (e) {}
    }
  }, [location.pathname]);

  // Fechar dropdown ao clicar fora (preservando a última página acessada)
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleMenuSignOut = (e) => {
    try {
      localStorage.removeItem('maptrees_last_menu_page');
    } catch (err) {}
    setIsViveiroMenu(false);
    setIsAdminMenu(false);
    setIsDropdownOpen(false);
    handleSignOut(e);
  };

  if (isAdminPath) {
    return null;
  }

  return (
    <div className="menu-container">
      <div className="logo-menu" onClick={toggleDropdown}>
        <img src={basicLogo} alt="Home" title="MapTrees" width="50" height="50" />
      </div>

      <div ref={dropdownRef} className={`dropdown-menu ${isDropdownOpen ? 'active' : ''}`}>
        {user && acceptUserTerms ? (
          <>
            <div className="menu-item-img">
              <Link to={`/userprofile/${user.uid}`} onClick={() => setIsDropdownOpen(false)}>
                <img
                  src={photoURL || placeholderAvatar}
                  title={displayFinal}
                  alt="Avatar"
                  className="menu-avatar"
                  width="80"
                  height="80"
                  onError={(e) => { e.target.src = placeholderAvatar; }}
                />
              </Link>
            </div>

            {(isAdminMenu && isAdmin) ? (
              <>
                {/* BOTÕES ADMINISTRATIVOS: MONITORAMENTO / CENTRAL DE COMUNICAÇÃO / MODERAÇÃO */}
                <div className="admin-icons-grid" style={{ marginTop: '8px' }}>
                  <Link
                    className="admin-grid-btn"
                    to="/admin-front-page?tab=dashboard"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Monitoramento"
                    aria-label="Monitoramento"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <path d="M7 16v-4" />
                      <path d="M12 16v-8" />
                      <path d="M17 16v-6" />
                    </svg>
                  </Link>

                  <Link
                    className="admin-grid-btn"
                    to="/admin-front-page?tab=communication"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Central de Comunicação"
                    aria-label="Central de Comunicação"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <path d="M8 10h.01" />
                      <path d="M12 10h.01" />
                      <path d="M16 10h.01" />
                    </svg>
                    {pendingCommunicationCount > 0 && (
                      <span className="notification-badge badge-danger">{pendingCommunicationCount}</span>
                    )}
                  </Link>

                  <Link
                    className="admin-grid-btn"
                    to="/admin-front-page?tab=moderation"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Moderação"
                    aria-label="Moderação"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {pendingReportsCount > 0 && (
                      <span className="notification-badge badge-danger">{pendingReportsCount}</span>
                    )}
                  </Link>
                </div>

                {/* USUÁRIOS */}
                <span className="admin-section-label">Usuários</span>
                <div className="admin-users-row">
                  <Link className="admin-pill-link" to="/admin-front-page?tab=users" onClick={() => setIsDropdownOpen(false)}>
                    Freemium
                  </Link>
                  <Link className="admin-pill-link" to="/admin-front-page?tab=proUsers" onClick={() => setIsDropdownOpen(false)}>
                    PRO
                  </Link>
                </div>

                {/* MUDAS */}
                <span className="admin-section-label">Mudas</span>
                <div className="admin-users-row">
                  <Link
                    className="admin-pill-link"
                    to="/pro/seedlings"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Gestão"
                  >
                    Gestão
                  </Link>
                  <Link
                    className="admin-pill-link"
                    to="/unifesp/localizar-muda"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Localizador"
                  >
                    Localizador
                  </Link>
                </div>

                {/* PREFEITURAS (ÍCONES SVG EM LINHA) */}
                <span className="admin-section-label">Prefeituras</span>
                <div className="admin-icons-grid">
                  <Link
                    className="admin-grid-btn"
                    to="/admin-front-page?tab=prefeituras"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Cidades & Prefeituras"
                    aria-label="Cidades & Prefeituras"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="21" x2="21" y2="21" />
                      <line x1="6" y1="21" x2="6" y2="11" />
                      <line x1="10" y1="21" x2="10" y2="11" />
                      <line x1="14" y1="21" x2="14" y2="11" />
                      <line x1="18" y1="21" x2="18" y2="11" />
                      <polygon points="12 3 2 9 22 9 12 3" />
                    </svg>
                  </Link>
                  <Link
                    className="admin-grid-btn"
                    to="/admin-front-page?tab=satellite"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Levantamento Satélite"
                    aria-label="Levantamento Satélite"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13 7 9 3 5 7l4 4" />
                      <path d="m17 11 4 4-4 4-4-4" />
                      <path d="m8 12 4 4 6-6-4-4Z" />
                      <path d="m16 8 3-3" />
                      <path d="M9 21a6 6 0 0 0-6-6" />
                    </svg>
                  </Link>
                  <Link
                    className="admin-grid-btn"
                    to="/admin-front-page?tab=satellite-street"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Satélite por Rua"
                    aria-label="Satélite por Rua"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 19L8 5h8l4 14" />
                      <line x1="12" y1="8" x2="12" y2="10" />
                      <line x1="12" y1="13" x2="12" y2="15" />
                      <line x1="12" y1="18" x2="12" y2="20" />
                    </svg>
                  </Link>
                </div>

                {/* INVENTÁRIO */}
                <span className="admin-section-label">Inventário</span>
                <div className="admin-users-row">
                  <Link
                    className="admin-pill-link admin-inventory-pill"
                    to="/admin-front-page?tab=trees"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Inventário"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      padding: "8px 12px",
                      fontWeight: 600,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 18h8a4 4 0 0 0 3.8-2.8 4 4 0 0 0-1.6-4.5 4.5 4.5 0 0 0-7.2-4.2A5 5 0 0 0 5 11.5a4 4 0 0 0 3 6.5z" />
                      <path d="M12 18v4" />
                    </svg>
                    <span>Inventário</span>
                  </Link>
                </div>

                {/* SISTEMA */}
                <span className="admin-section-label">Sistema</span>
                <div className="admin-icons-grid grid-2">
                  <Link
                    className="admin-grid-btn"
                    to="/admin-front-page?tab=settings"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Badges e Certificado"
                    aria-label="Badges e Certificado"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="6" />
                      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                    </svg>
                  </Link>
                  <Link
                    className="admin-grid-btn"
                    to="/admin-porte"
                    onClick={() => setIsDropdownOpen(false)}
                    title="Porte"
                    aria-label="Porte"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="4" y1="4" x2="4" y2="20" />
                      <polyline points="2 7 4 4 6 7" />
                      <polyline points="2 17 4 20 6 17" />
                      <path d="M10 18h8a3.5 3.5 0 0 0 3-1.8 3.5 3.5 0 0 0-1-4.2A4 4 0 0 0 14 8a4 4 0 0 0-4 4.5" />
                      <path d="M14 18v3" />
                    </svg>
                  </Link>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--background-divider, #dee2e6)', margin: '8px 0' }} />

                <button
                  type="button"
                  className="menu-item"
                  onClick={() => {
                    setIsAdminMenu(false);
                    try {
                      localStorage.setItem('maptrees_last_menu_page', 'main');
                    } catch (e) {}
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    font: 'inherit',
                    cursor: 'pointer',
                    padding: '8px 10px',
                    color: 'var(--text-secondary, #6c757d)'
                  }}
                >
                  ← Menu Principal
                </button>

                <Link onClick={handleMenuSignOut} className="menu-item">{texts.sign_out}</Link>
              </>
            ) : (isViveiroMenu && isProViveiro) ? (
              <>
                <span className="admin-section-label" style={{ fontSize: '0.88rem', color: '#15803d', fontWeight: 'bold' }}>
                  🌿 {workgroupLabel}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '6px' }}>
                  {/* Gestão de Mudas */}
                  <Link
                    className="menu-item"
                    to="/pro/seedlings"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    Gestão de Mudas
                  </Link>

                  {/* Localizar Muda (Unifesp) */}
                  {isUnifespWorkgroup && (
                    <Link
                      className="menu-item"
                      to="/unifesp/localizar-muda"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      🔍 Localizar Muda
                    </Link>
                  )}

                  {/* Gestão de Equipe */}
                  {(!isProTecnico || allowedFeatures.teamManagement) && (
                    <Link
                      className="menu-item"
                      to="/pro/management"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {isProTecnico ? "Equipe" : "Gestão de Equipe"}
                    </Link>
                  )}

                  {/* Remessas */}
                  <Link
                    className="menu-item"
                    to="/pro/seedlings?tab=shipments"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    Remessas
                  </Link>

                  {/* Campos fechados */}
                  {allowedFeatures.confidentialFields && (
                    <Link
                      className="menu-item"
                      to="/pro/confidential-fields"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      Campos fechados
                    </Link>
                  )}

                  {/* Clientes */}
                  <Link
                    className="menu-item"
                    to="/pro/seedlings?tab=clients"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    Clientes
                  </Link>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--background-divider, #dee2e6)', margin: '8px 0' }} />

                <button
                  type="button"
                  className="menu-item"
                  onClick={() => {
                    setIsViveiroMenu(false);
                    try {
                      localStorage.setItem('maptrees_last_menu_page', 'main');
                    } catch (e) {}
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    font: 'inherit',
                    cursor: 'pointer',
                    padding: '8px 10px',
                    color: 'var(--text-secondary, #6c757d)'
                  }}
                >
                  ← Menu Principal
                </button>

                <Link onClick={handleMenuSignOut} className="menu-item">{texts.sign_out}</Link>
              </>
            ) : (
              <>
                <div className="menu-icons-row">
                  <Link to="/" onClick={() => setIsDropdownOpen(false)} className="icon-link">
                    <img src={ico_map} alt="Home" className="icon-img" width="24" height="24" />
                  </Link>

                  <Link to="/community" onClick={() => setIsDropdownOpen(false)} className="icon-link">
                    <img src={ico_community} alt="Community" className="icon-img" width="24" height="24" />
                  </Link>

                  <Link to="/feed" onClick={() => setIsDropdownOpen(false)} className="icon-link">
                    <img src={ico_gallery} alt="Feed" className="icon-img" width="24" height="24" />
                  </Link>

                  <div className="icon-link notification-wrapper">
                    <Link
                      id="centralnotifications"
                      to="/centralnotifications"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <img src={ico_notifications} alt="Notifications" className="icon-img" width="24" height="24" />
                    </Link>

                    {notificationsCount > 0 && (
                      <span className="notification-badge">{notificationsCount}</span>
                    )}
                  </div>
                </div>

                {/* <label className="label-area">{texts.trees}</label> */}
                {(isProUser || isAdmin) && (
                  <Link className="menu-item" to="/plantseedling" onClick={() => setIsDropdownOpen(false)}> {texts.plant}</Link>
                )}
                <Link className="menu-item" to="/addtree" onClick={() => setIsDropdownOpen(false)}> {texts.add_tree}</Link>
                <Link className="menu-item" to="/identifytrees" onClick={() => setIsDropdownOpen(false)}> {texts.identify || "Identificar"}</Link>

                <Link id="specieIndex" className="menu-item" to="/specie-index" onClick={() => setIsDropdownOpen(false)}>{texts.specie_index}</Link>
                <Link id="nurserieIndex" className="menu-item" to="/nurseries" onClick={() => setIsDropdownOpen(false)}>{texts.nurseries}</Link>

                <Link id="blog" className="menu-item" to="/blog" onClick={() => setIsDropdownOpen(false)}>{texts.blog_tutorials || "Blog / Tutoriais"}</Link>

                {isProViveiro ? (
                  <>
                    <hr style={{ border: 'none', borderTop: '1px solid var(--background-divider, #dee2e6)', margin: '8px 0' }} />
                    <button
                      type="button"
                      className="menu-item menu-item-viveiro-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsViveiroMenu(true);
                        setIsAdminMenu(false);
                        try {
                          localStorage.setItem('maptrees_last_menu_page', 'viveiro');
                        } catch (err) {}
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        width: '100%',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        font: 'inherit',
                        cursor: 'pointer',
                        padding: '8px 10px',
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{workgroupLabel}</span>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>›</span>
                    </button>
                  </>
                ) : (
                  (isProManagement || isPrefeitura) && (
                    <>
                      <label className="label-area">{workgroupLabel}</label>
                      {isProManagement && !isProTecnico && (
                        <>
                          {allowedFeatures.teamManagement && (
                            <Link className="menu-item" to="/pro/management" onClick={() => setIsDropdownOpen(false)}>
                              Gestão de Equipe
                            </Link>
                          )}
                          <Link className="menu-item" to="/pro/seedlings" onClick={() => setIsDropdownOpen(false)}>
                            Gestão de Mudas
                          </Link>
                          {isUnifespWorkgroup && (
                            <Link className="menu-item" to="/unifesp/localizar-muda" onClick={() => setIsDropdownOpen(false)}>
                              🔍 Localizar Muda
                            </Link>
                          )}
                          {allowedFeatures.confidentialFields && (
                            <Link className="menu-item" to="/pro/confidential-fields" onClick={() => setIsDropdownOpen(false)}>
                              Campos fechados
                            </Link>
                          )}
                        </>
                      )}
                      {isProTecnico && (
                        <>
                          {allowedFeatures.teamManagement && (
                            <Link className="menu-item" to="/pro/management" onClick={() => setIsDropdownOpen(false)}>
                              Equipe
                            </Link>
                          )}
                          <Link className="menu-item" to="/pro/seedlings" onClick={() => setIsDropdownOpen(false)}>
                            Gestão de Mudas
                          </Link>
                          {isUnifespWorkgroup && (
                            <Link className="menu-item" to="/unifesp/localizar-muda" onClick={() => setIsDropdownOpen(false)}>
                              🔍 Localizar Muda
                            </Link>
                          )}
                          {allowedFeatures.confidentialFields && (
                            <Link className="menu-item" to="/pro/confidential-fields" onClick={() => setIsDropdownOpen(false)}>
                              Campos fechados
                            </Link>
                          )}
                        </>
                      )}
                      {isPrefeitura && (
                        <Link className="menu-item" to="/pro/city-dashboard" onClick={() => setIsDropdownOpen(false)}>
                          Painel da Cidade
                        </Link>
                      )}
                    </>
                  )
                )}

                {isAdmin && (
                  <>
                    <hr style={{ border: 'none', borderTop: '1px solid var(--background-divider, #dee2e6)', margin: '8px 0' }} />
                    <button
                      type="button"
                      className="menu-item menu-item-admin-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAdminMenu(true);
                        setIsViveiroMenu(false);
                        try {
                          localStorage.setItem('maptrees_last_menu_page', 'admin');
                        } catch (err) {}
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        width: '100%',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        font: 'inherit',
                        cursor: 'pointer',
                        padding: '8px 10px',
                      }}
                    >
                      <span>Administrativo</span>
                      <span style={{ fontSize: '15px', color: 'var(--text-secondary)' }}>›</span>
                    </button>
                    <Link
                      className="menu-item-admin-sub"
                      to="/admin-front-page?tab=communication"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span>{texts.communication || "Comunicação"}</span>
                      {pendingCommunicationCount > 0 && (
                        <span className="pending-badge-circle">{pendingCommunicationCount}</span>
                      )}
                    </Link>
                    <Link
                      className="menu-item-admin-sub"
                      to="/admin-front-page?tab=moderation"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span>{texts.moderation || "Moderação"}</span>
                      {pendingReportsCount > 0 && (
                        <span className="pending-badge-circle">{pendingReportsCount}</span>
                      )}
                    </Link>
                  </>
                )}

                <br />
                <Link onClick={handleMenuSignOut} className="menu-item">{texts.sign_out}</Link>
              </>
            )}
          </>
        ) : user && !acceptUserTerms ? (
          <div className="incomplete-signup-prompt">
            <Link
              to="/addUser"
              className="menu-item-profile"
              onClick={() => setIsDropdownOpen(false)}
            >
              Completar Cadastro
            </Link>
            <Link onClick={handleSignOut} className="menu-item">{texts.sign_out}</Link>
          </div>
        ) : (
          <>
            <button
              className="btn-google-premium"
              onClick={() => {
                setIsDropdownOpen(false);
                signInWithGoogle();
              }}
            >
              <img
                src={googleIco}
                alt="Google Logo"
                className="google-icon-premium"
                loading="lazy"
              />
              <span>{texts.connect || "Conectar"}</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
